import { firebaseConfig, resolveFirebaseConfig } from '../core/firebaseConfig.js';
import { rbacController } from './rbacController.js';
import { renderProfileMenu } from '../ui/profileMenu.js';

class AuthService {
    constructor() {
        this.app = null;
        this.auth = null;
        this.currentUser = null;
        this.userRole = 'editor';
        this.authState = 'initializing';
        this.isLocalMode = localStorage.getItem('localMode') === 'true';
    }

    isLocalHost() {
        return typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' ||
             window.location.hostname === '127.0.0.1');
    }

    canUseLocalMode() {
        return this.isLocalHost();
    }

    async _loadScript(src) {
        return new Promise((resolve, reject) => {
            // Check if already registered/loaded
            if (src.includes('firebase-app-compat.js') && typeof firebase !== 'undefined') return resolve();
            if (src.includes('firebase-auth-compat.js') && typeof firebase !== 'undefined' && typeof firebase.auth === 'function') return resolve();
            if (src.includes('firebase-firestore-compat.js') && typeof firebase !== 'undefined' && typeof firebase.firestore === 'function') return resolve();

            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    }

    async init() {
        const isLocalHost = this.isLocalHost();
        if (!this.canUseLocalMode()) {
            document.getElementById('btn-local-mode')?.classList.add('hidden');
        }

        if (localStorage.getItem('localMode') === 'true') {
            if (!this.canUseLocalMode()) {
                console.warn("[Auth] Ignoring persisted localMode on production host.");
                localStorage.removeItem('localMode');
            } else {
                console.log("[Auth] localMode is active. Directly starting local mode without delay.");
                this.startLocalMode();
                return;
            }
        }

        try {
            // Support for E2E Mock Authentication state injection (restricted strictly to local test hosts to prevent production/preview bypass)
            if (isLocalHost && window.mockAuthUser !== undefined) {
                const runMockAuth = () => {
                    const user = window.mockAuthUser;
                    if (user) {
                        this.authState = 'authenticated';
                        this.onLoginSuccess(user);
                    } else {
                        this.authState = 'unauthenticated';
                        this.showLoginView();
                        this.hideSplashScreen();
                    }
                };
                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', runMockAuth);
                } else {
                    runMockAuth();
                }
                return;
            }

            // Sequentially load Firebase SDK components to bypass asynchronous registration races
            if (typeof firebase === 'undefined' || typeof firebase.auth !== 'function') {
                console.log("Dynamically loading Firebase SDKs sequentially...");
                await this._loadScript("https://www.gstatic.com/firebasejs/10.8.0/firebase-app-compat.js");
                await this._loadScript("https://www.gstatic.com/firebasejs/10.8.0/firebase-auth-compat.js");
                await this._loadScript("https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore-compat.js");
            }

            const resolvedConfig = await resolveFirebaseConfig();
            if (!resolvedConfig.apiKey) {
                throw new Error("Firebase Auth is not configured. Set FIREBASE_API_KEY in Cloudflare Pages secrets.");
            }

            if (!firebase.apps.length) {
                this.app = firebase.initializeApp(firebaseConfig);
            } else {
                this.app = firebase.app();
            }
            this.auth = firebase.auth();
            
            // 2.5-second safety timeout for offline fallback
            const splashTimeout = setTimeout(() => {
                if (!document.getElementById('initial-splash').classList.contains('hidden')) {
                    if (this.canUseLocalMode()) {
                        console.warn("Auth initialization timed out. Switching to Local Failsafe Mode.");
                        this.startLocalMode();
                    } else {
                        console.warn("Auth initialization timed out. Showing login view without local fallback.");
                        this.authState = 'unauthenticated';
                        this.showLoginView();
                        this.hideSplashScreen();
                    }
                }
            }, 2500);

            // Listen for auth state changes
            this.auth.onAuthStateChanged(user => {
                clearTimeout(splashTimeout);
                
                // If logout is in progress, ignore any login signals
                if (sessionStorage.getItem('auraDoc_logout_in_progress')) {
                    if (user) {
                        this.logout();
                        return;
                    } else {
                        sessionStorage.removeItem('auraDoc_logout_in_progress');
                    }
                }

                if (user) {
                    this.authState = 'authenticated';
                    this.onLoginSuccess(user);
                } else {
                    this.authState = 'unauthenticated';
                    this.showLoginView();
                    this.hideSplashScreen();
                }
            });
        } catch (error) {
            console.warn("Firebase init failed, displaying local-enabled login view", error);
            this.authState = 'unauthenticated';
            this.showLoginView();
            this.hideSplashScreen();
        }
    }

    onLoginSuccess(user) {
        console.log("Login success:", user.email);
        
        this.currentUser = user;
        const email = user.email || '';
        const isAdmin = email === 'vivaura.tech@gmail.com' || email.startsWith('admin@') || email.includes('admin');
        this.userRole = isAdmin ? 'admin' : 'editor';
        
        if (isAdmin) {
            rbacController.setRole(rbacController.roles.ADMIN);
            console.log("RBAC: ADMIN role assigned");
        } else {
            rbacController.setRole(rbacController.roles.EDITOR);
            console.log("RBAC: EDITOR role assigned");
        }

        const appWrap = document.getElementById('app-wrapper');
        const authMod = document.getElementById('auth-modal-container');

        if (authMod) authMod.classList.add('modal-hidden');
        if (appWrap) {
            appWrap.classList.remove('hidden');
            appWrap.classList.add('flex');
        }

        this.hideSplashScreen();
        localStorage.setItem('localMode', 'false');
        
        try {
            renderProfileMenu();
            syncRoleBasedUI();
        } catch (e) {
            console.error("UI Sync error:", e);
        }
    }

    async handleLogin(email, password) {
        if (!this.auth) {
            if (this.canUseLocalMode()) return this.startLocalMode();
            throw new Error("Firebase Auth is not initialized. Check Firebase API key configuration.");
        }
        try {
            const result = await this.auth.signInWithEmailAndPassword(email, password);
            this.onLoginSuccess(result.user);
        } catch (error) {
            const errEl = document.getElementById('login-error-msg');
            if (errEl) {
                errEl.innerText = "로그인 실패: " + error.message;
                errEl.classList.remove('hidden');
            }
        }
    }

    async handleSignup(email, password) {
        if (!this.auth) {
            if (this.canUseLocalMode()) return this.startLocalMode();
            throw new Error("Firebase Auth is not initialized. Check Firebase API key configuration.");
        }
        try {
            const result = await this.auth.createUserWithEmailAndPassword(email, password);
            alert("회원가입이 완료되었습니다!");
            this.onLoginSuccess(result.user);
        } catch (error) {
            const errEl = document.getElementById('signup-error-msg');
            if (errEl) {
                errEl.innerText = "가입 실패: " + error.message;
                errEl.classList.remove('hidden');
            }
        }
    }

    async handleGoogleLogin() {
        if (!this.auth) {
            if (this.canUseLocalMode()) return this.startLocalMode();
            throw new Error("Firebase Auth is not initialized. Check Firebase API key configuration.");
        }
        try {
            const provider = new firebase.auth.GoogleAuthProvider();
            const result = await this.auth.signInWithPopup(provider);
            this.onLoginSuccess(result.user);
        } catch (error) {
            alert("Google 로그인 실패: " + error.message);
        }
    }

    async logout() {
        sessionStorage.setItem('auraDoc_logout_in_progress', 'true');
        localStorage.removeItem('localMode');
        
        document.getElementById('app-wrapper')?.classList.add('hidden');
        document.getElementById('initial-splash')?.classList.remove('hidden');
        
        if (this.auth) {
            try {
                await this.auth.signOut();
                window.location.replace('index.html');
            } catch (err) {
                console.error("Logout error:", err);
                window.location.replace('index.html');
            }
        } else {
            window.location.replace('index.html');
        }
    }

    startLocalMode() {
        if (!this.canUseLocalMode()) {
            console.warn("[Auth] Local mode is disabled on production hosts.");
            localStorage.removeItem('localMode');
            this.authState = 'unauthenticated';
            this.showLoginView();
            const errEl = document.getElementById('login-error-msg');
            if (errEl) {
                errEl.innerText = "운영 도메인에서는 로컬 모드를 사용할 수 없습니다. 로그인 설정을 확인해 주세요.";
                errEl.classList.remove('hidden');
            }
            this.hideSplashScreen();
            return;
        }
        console.log("Starting local mode...");
        this.authState = 'authenticated';
        localStorage.setItem('localMode', 'true');
        rbacController.setRole(rbacController.roles.ADMIN); // Local defaults to admin for development
        
        const appWrap = document.getElementById('app-wrapper');
        const authMod = document.getElementById('auth-modal-container');
        const uInfo = document.getElementById('user-info');
        const sync = document.getElementById('sync-status');

        if (authMod) authMod.classList.add('modal-hidden');
        if (appWrap) {
            appWrap.classList.remove('hidden');
            appWrap.classList.add('flex');
        }
        if (uInfo) uInfo.innerText = "Local User";
        if (sync) {
            sync.innerText = "⚠️ 로컬 모드";
            sync.classList.add('bg-amber-100', 'text-amber-700');
        }
        this.hideSplashScreen();
        
        try {
            renderProfileMenu();
            syncRoleBasedUI();
        } catch (e) {
            console.error("UI Sync error:", e);
        }
    }

    hideSplashScreen() {
        const splash = document.getElementById('initial-splash');
        if (splash) {
            splash.classList.add('opacity-0');
            setTimeout(() => splash.classList.add('hidden'), 300);
        }
    }

    showSignupView() {
        if (this.authState === 'initializing') return;
        document.getElementById('login-view')?.classList.add('hidden');
        document.getElementById('signup-view')?.classList.remove('hidden');
    }

    showLoginView() {
        if (this.authState === 'initializing') {
            console.log("[Auth] showLoginView ignored during initialization state");
            return;
        }
        document.getElementById('signup-view')?.classList.add('hidden');
        document.getElementById('login-view')?.classList.remove('hidden');
        document.getElementById('auth-modal-container')?.classList.remove('modal-hidden');
    }
}

export function syncRoleBasedUI() {
    const role = rbacController.getUserRole();
    const isAdmin = role === rbacController.roles.ADMIN;
    
    console.log(`[RBAC] Syncing UI elements for role: ${role}`);
    
    const navSourcing = document.getElementById('nav-sourcing');
    const modeToggle = document.querySelector('.mode-toggle');
    const modeSlider = document.getElementById('mode-slider');
    
    if (navSourcing) {
        navSourcing.classList.remove('hidden');
        if (modeToggle) {
            modeToggle.style.width = '240px';
        }
        if (modeSlider) {
            modeSlider.style.width = '114px';
        }
    }
    
    const btnAdmin = document.getElementById('btn-open-admin');
    const btnSettings = document.getElementById('btn-open-settings');
    const userRoleDisplay = document.getElementById('user-role-display');
    
    if (btnAdmin) {
        if (isAdmin) {
            btnAdmin.classList.remove('hidden');
        } else {
            btnAdmin.classList.add('hidden');
        }
    }
    
    if (btnSettings) {
        if (isAdmin) {
            btnSettings.classList.remove('hidden');
        } else {
            btnSettings.classList.add('hidden');
        }
    }
    
    if (userRoleDisplay) {
        userRoleDisplay.innerText = isAdmin ? "👑 시스템 관리자" : "👤 일반 사용자";
    }
}

export const authService = new AuthService();

// Note: authService.init() should be called by main.js to avoid double initialization.
